import { apiError } from '../apiError';
import axiosInstance from '../axios.config';
import { API_ENDPOINTS } from '../endpoints';
import { ApiResponse } from 'types';

/**
 * IVR app registry — one record per configured flow (operator_id, service_id).
 * Backend: teeoff-cpass-backend /telephony/ivr/apps. Apps are imported from the
 * runtime state_details or created/edited here.
 */
export interface IvrApp {
  id?: number;
  operator_id: string;
  service_id: string;
  name?: string;
  description?: string;
  business_name?: string;
  status?: 'ACTIVE' | 'DRAFT' | 'INACTIVE' | string;
  languages?: string;     // csv
  directions?: string;    // csv: inbound,outbound
  state_count?: number;
  source?: string;        // RUNTIME_IMPORT | MANUAL | AUTO
  created_at?: string;
  updated_at?: string;
}

export interface ImportSummary {
  flows: number;
  created: number;
  updated: number;
  skipped: number;
}

// Shared so a 403 reads as a refusal rather than a load failure.
const err = (e: any, msg: string) => apiError(e, msg);

export const ivrAppsService = {
  list: async (operatorId?: string): Promise<ApiResponse<IvrApp[]>> => {
    try { return (await axiosInstance.get(API_ENDPOINTS.IVR_APPS, { params: operatorId ? { operatorId } : {} })).data; }
    catch (e: any) { return err(e, 'Failed to load IVR apps'); }
  },
  upsert: async (data: IvrApp): Promise<ApiResponse<IvrApp>> => {
    try { return (await axiosInstance.post(API_ENDPOINTS.IVR_APPS, data)).data; }
    catch (e: any) { return err(e, 'Failed to save IVR app'); }
  },
  remove: async (id: number): Promise<ApiResponse<void>> => {
    try { return (await axiosInstance.delete(API_ENDPOINTS.IVR_APP_DETAIL(id))).data; }
    catch (e: any) { return err(e, 'Failed to delete IVR app'); }
  },
  // Backfill the REGISTRY from all flows configured in the runtime state_details.
  //
  // ⚠️ THIS DOES NOT COPY FLOW STATES, and the distinction has already cost
  // time. The backend says so in its own response: "App records only. Flow
  // states are not copied." It writes ivr_apps rows — operator, service,
  // languages, state count — and nothing the BUILDER reads. So a screen that
  // offers only this button, to somebody whose editor is blank, reports
  // "Imported" and changes nothing they can see. Use importFlow below for that.
  importFromRuntime: async (): Promise<ApiResponse<ImportSummary>> => {
    try { return (await axiosInstance.post(API_ENDPOINTS.IVR_APPS_IMPORT)).data; }
    catch (e: any) { return err(e, 'Import failed'); }
  },

  /**
   * Rebuild ONE flow's nodes from what the switch is actually running.
   *
   * This is the import the builder cares about: it writes ivr_flows.nodes, which
   * is what the editor loads. Needed after any importer fix, because an import
   * is a write and re-running it is the only thing that rebuilds that JSON — a
   * fix cannot reach backwards into rows the old code already wrote.
   *
   * ONE FLOW AT A TIME, by design and not by oversight. A switch hosts many
   * tenants (tata-landline runs 142 flows across 77 businesses), so the backend
   * refuses to apply without an operator AND service: importing unfiltered would
   * file every business's flow under whichever organisation asked. Preview
   * (apply=false) is unfiltered on purpose — seeing the whole switch is the
   * point of a preview.
   */
  importFlow: async (params: {
    providerId: number; organizationId: string; operatorId: string; serviceId: string;
    apply?: boolean;
  }): Promise<ApiResponse<any>> => {
    const q = new URLSearchParams({
      apply: String(params.apply ?? false),
      organizationId: params.organizationId,
      operatorId: params.operatorId,
      serviceId: params.serviceId,
    });
    try {
      return (await axiosInstance.post(
        `/ivr/import/provider/${params.providerId}?${q.toString()}`)).data;
    } catch (e: any) { return err(e, 'Could not re-import that flow'); }
  },
};
